import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Text, PrimaryButton, DefaultButton, IconButton, MessageBar, MessageBarType, Spinner, SpinnerSize, Shimmer, ShimmerElementType, Stack, CommandBar, ICommandBarItemProps, Panel, PanelType } from '@fluentui/react';
import { api } from '../services/api';
import type { AgentBox, EnvironmentConfig } from '../types';
import { getDefaultToolSelection, type ToolSelection } from '../types/tools';
import type { SessionInfo } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import FormField from '../components/FormField';
import ToolSelector from '../components/ToolSelector';
import EnvironmentPicker from '../components/EnvironmentPicker';
import styles from './BoxDetail.module.css';


export default function BoxDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialOperationId = searchParams.get('operationId');
  const [box, setBox] = useState<AgentBox | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [destroying, setDestroying] = useState(false);
  const [operationId, setOperationId] = useState<string | null>(initialOperationId);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [creatingSess, setCreatingSess] = useState(false);
  const [toolSelection, setToolSelection] = useState<ToolSelection>(getDefaultToolSelection);
  const [toolsPanelOpen, setToolsPanelOpen] = useState(false);
  const [environmentConfigs, setEnvironmentConfigs] = useState<EnvironmentConfig[]>([]);
  const [envPanelOpen, setEnvPanelOpen] = useState(false);
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username || '';

  const fetchBox = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.getInstance(id);
      setBox(data);
      setError('');
    } catch (err) {
      const isNotFound = err instanceof Error && err.message === 'API error: 404';
      if (!isNotFound) {
        setError(err instanceof Error ? err.message : 'Failed to load AgentBox');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchBox(); }, [fetchBox]);

  useEffect(() => {
    const isTransitional = box?.status === 'Creating' || box?.status === 'Destroying';
    const isAwaitingInitialCreate = !destroying && !box && !!operationId;
    if (!isTransitional && !destroying && !isAwaitingInitialCreate) return;
    const interval = setInterval(fetchBox, 3000);
    return () => clearInterval(interval);
  }, [box?.status, destroying, fetchBox, operationId]);

  useEffect(() => {
    if (!operationId || destroying) return;
    const interval = setInterval(async () => {
      try {
        const op = await api.getOperation(operationId);
        if (op.status === 'Succeeded') {
          setOperationId(null);
          setSearchParams((current) => { const next = new URLSearchParams(current); next.delete('operationId'); return next; }, { replace: true });
          await fetchBox();
        } else if (op.status === 'Failed') {
          setOperationId(null);
          setSearchParams((current) => { const next = new URLSearchParams(current); next.delete('operationId'); return next; }, { replace: true });
          setLoading(false);
          setError(op.error || 'Failed to create AgentBox');
        }
      } catch { /* Keep polling */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [operationId, destroying, fetchBox, setSearchParams]);

  useEffect(() => {
    if (!operationId || !destroying) return;
    const interval = setInterval(async () => {
      try {
        const op = await api.getOperation(operationId);
        if (op.status === 'Succeeded') { setDestroying(false); setOperationId(null); navigate('/'); }
        else if (op.status === 'Failed') { setDestroying(false); setOperationId(null); setError(op.error || 'Failed to destroy AgentBox'); }
      } catch { /* Keep polling */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [operationId, destroying, navigate]);

  const [sshConnecting, setSshConnecting] = useState(false);
  const [sshInfo, setSshInfo] = useState<{ privateKeyPem: string; certificate: string } | null>(null);
  const [, setTick] = useState(0);
  useEffect(() => { const interval = setInterval(() => setTick((t) => t + 1), 60_000); return () => clearInterval(interval); }, []);

  async function handleDestroy() {
    if (!id) return;
    if (!window.confirm(`Destroy AgentBox #${id}? This cannot be undone.`)) return;
    try {
      setDestroying(true);
      const { operationId: opId } = await api.destroyInstance(id);
      setOperationId(opId);
    } catch (err) {
      setDestroying(false);
      setError(err instanceof Error ? err.message : 'Failed to destroy AgentBox');
    }
  }

  function copySshConfig() {
    if (!box) return;
    const config = `Host ${box.id}.agentbox\n  HostName ${box.fqdn}\n  Port 2222\n  User agentbox`;
    navigator.clipboard.writeText(config).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  const fetchSessions = useCallback(async () => {
    if (!id || !box || box.status !== 'Running') return;
    try { const data = await api.listSessions(id); setSessions(data); } catch { /* Ignore */ }
  }, [id, box?.status]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);
  useEffect(() => { if (box?.status !== 'Running') return; const interval = setInterval(fetchSessions, 10000); return () => clearInterval(interval); }, [box?.status, fetchSessions]);

  async function handleCreateSession() {
    if (!id) return;
    try {
      setCreatingSess(true);
      const nextNum = sessions.length + 1;
      const name = `session-${nextNum}`;
      await api.createSession(id, name);
      await fetchSessions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    } finally { setCreatingSess(false); }
  }

  async function handleDeleteSession(name: string) {
    if (!id) return;
    if (!window.confirm(`Delete session "${name}"?`)) return;
    try { await api.deleteSession(id, name); await fetchSessions(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to delete session'); }
  }

  async function handleSshConnect() {
    if (!id) return;
    setSshConnecting(true);
    setSshInfo(null);
    try {
      const keyPair = await window.crypto.subtle.generateKey(
        { name: 'RSASSA-PKCS1-v1_5', modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-512' },
        true,
        ['sign', 'verify'],
      );

      const pkcs8 = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
      const pkcs8B64 = btoa(String.fromCharCode(...new Uint8Array(pkcs8)));
      const privateKeyPem = `-----BEGIN PRIVATE KEY-----\n${pkcs8B64.match(/.{1,64}/g)!.join('\n')}\n-----END PRIVATE KEY-----`;

      const spki = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
      const sshPublicKeyBase64 = spkiToSshPublicKey(spki);

      const { certificate } = await api.signSshKey(id, `ssh-rsa ${sshPublicKeyBase64}`);
      setSshInfo({ privateKeyPem, certificate });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate SSH credentials');
    } finally {
      setSshConnecting(false);
    }
  }

  function spkiToSshPublicKey(spki: ArrayBuffer): string {
    const der = new Uint8Array(spki);
    let pos = 0;

    function readLen(): number {
      const b = der[pos++];
      if (b < 0x80) return b;
      let len = 0;
      for (let i = 0; i < (b & 0x7f); i++) len = (len << 8) | der[pos++];
      return len;
    }
    function skip() { pos++; }

    skip(); readLen();
    skip(); const algLen = readLen(); pos += algLen;
    skip(); readLen(); pos++;
    skip(); readLen();
    skip(); const nLen = readLen(); const n = der.slice(pos, pos + nLen); pos += nLen;
    skip(); const eLen = readLen(); const e = der.slice(pos, pos + eLen);

    function sshStr(data: Uint8Array): Uint8Array {
      const out = new Uint8Array(4 + data.length);
      new DataView(out.buffer).setUint32(0, data.length, false);
      out.set(data, 4);
      return out;
    }

    const keyType = new TextEncoder().encode('ssh-rsa');
    const parts = [sshStr(keyType), sshStr(e), sshStr(n)];
    const total = parts.reduce((s, p) => s + p.length, 0);
    const wire = new Uint8Array(total);
    let off = 0;
    for (const p of parts) { wire.set(p, off); off += p.length; }

    return btoa(String.fromCharCode(...wire));
  }

  function downloadText(filename: string, content: string) {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  function formatAge(unixTs: number): string {
    if (!unixTs) return '';
    const diff = Date.now() - unixTs * 1000;
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    return `${Math.floor(diff / 3_600_000)}h ago`;
  }

  function formatDate(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function formatExpiry(iso: string): string {
    if (!iso) return '—';
    const diff = new Date(iso).getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    return `${h}h ${m}m remaining`;
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <CommandBar items={[]} farItems={[]} styles={{ root: { borderBottom: '1px solid var(--ab-border)', background: 'var(--ab-bg-command-bar)', padding: '0 8px' } }} />
        <div className={styles.body}>
          <div className={styles.fieldGrid}>
            <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: '60%', height: 14 }]} />
            <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: '50%', height: 14 }]} />
            <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: '40%', height: 14 }]} />
            <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: '55%', height: 14 }]} />
          </div>
        </div>
      </div>
    );
  }

  if (error && !box) {
    return (
      <div className={styles.page}>
        <CommandBar
          items={[{ key: 'back', iconOnly: true, iconProps: { iconName: 'Back' }, onClick: () => { navigate('/'); }, ariaLabel: 'Back' }]}
          styles={{ root: { borderBottom: '1px solid var(--ab-border)', background: 'var(--ab-bg-command-bar)', padding: '0 8px' } }}
        />
        <div className={styles.body}>
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')}>{error}</MessageBar>
        </div>
      </div>
    );
  }

  const createPending = !destroying && !!operationId;

  if (!box || box.status === 'Creating' || createPending) {
    return (
      <div className={styles.page}>
        <CommandBar
          items={[{ key: 'back', iconOnly: true, iconProps: { iconName: 'Back' }, onClick: () => { navigate('/'); }, ariaLabel: 'Back' }]}
          styles={{ root: { borderBottom: '1px solid var(--ab-border)', background: 'var(--ab-bg-command-bar)', padding: '0 8px' } }}
        />
        <div className={styles.formHeader}>
          <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>#{id}</Text>
          <StatusBadge status="Creating" />
        </div>
        <div className={styles.provisioningCard}>
          <Spinner size={SpinnerSize.large} />
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
            {box ? 'Starting terminal and editor…' : 'Provisioning your instance…'}
          </Text>
          <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
            {box ? 'The container exists, but we are waiting until the terminal and editor are actually reachable.' : 'This usually takes 15–30 seconds. The page will update automatically.'}
          </Text>
        </div>
      </div>
    );
  }

  const commandBarItems: ICommandBarItemProps[] = [
    { key: 'back', iconOnly: true, iconProps: { iconName: 'Back' }, onClick: () => { navigate('/'); }, ariaLabel: 'Back' },
    ...(box.status === 'Running' ? [
      { key: 'editor', text: 'VS Code', iconProps: { iconName: 'Code' }, onClick: () => { navigate(`/instances/${id}/editor`); } },
    ] as ICommandBarItemProps[] : []),
    { key: 'destroy', text: 'Delete', iconProps: { iconName: 'Delete' }, onClick: () => { handleDestroy(); }, className: styles.dangerBtn },
  ];

  return (
    <div className={styles.page}>
      <CommandBar items={commandBarItems} styles={{ root: { borderBottom: '1px solid var(--ab-border)', background: 'var(--ab-bg-command-bar)', padding: '0 8px' } }} />

      <div className={styles.formHeader}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>#{box.id}</Text>
        <StatusBadge status={box.status} />
      </div>

      <div className={styles.body}>
        {error && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')} isMultiline={false}>{error}</MessageBar>
        )}

        {/* Field-value grid */}
        <div className={styles.fieldGrid}>
          <FormField label="Status"><StatusBadge status={box.status} /></FormField>
          <FormField label="Owner"><Text>{box.ownerName || box.owner}</Text></FormField>
          <FormField label="Created"><Text>{formatDate(box.createdAt)}</Text></FormField>
          <FormField label="Expires"><Text>{formatExpiry(box.expiresAt)}</Text></FormField>
          <FormField label="FQDN">
            <Text styles={{ root: { color: 'var(--ab-text-link)', wordBreak: 'break-all' } }}>
              {box.fqdn}
            </Text>
          </FormField>
        </div>

        {/* Tools section */}
        <div className={styles.section}>
          <div className={styles.toolsSectionHeader}>
            <Text variant="mediumPlus" className={styles.sectionHeader}>Tools</Text>
            <DefaultButton
              text="Configure"
              iconProps={{ iconName: 'Settings' }}
              onClick={() => setToolsPanelOpen(true)}
              styles={{ root: { minWidth: 0 } }}
            />
          </div>
          <ToolSelector value={toolSelection} onChange={setToolSelection} readOnly environmentCount={environmentConfigs.length} />
        </div>

        <Panel
          isOpen={toolsPanelOpen}
          type={PanelType.medium}
          onDismiss={() => setToolsPanelOpen(false)}
          headerText="Configure Tools"
          onRenderFooterContent={() => (
            <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { padding: '12px 0' } }}>
              <PrimaryButton text="Save" onClick={() => setToolsPanelOpen(false)} />
              <DefaultButton text="Cancel" onClick={() => setToolsPanelOpen(false)} />
            </Stack>
          )}
          isFooterAtBottom
        >
          <div style={{ paddingTop: 16 }}>
            <ToolSelector value={toolSelection} onChange={setToolSelection} environmentCount={environmentConfigs.length} />
          </div>
        </Panel>

        {/* Environments section */}
        <div className={styles.section}>
          <div className={styles.toolsSectionHeader}>
            <Text variant="mediumPlus" className={styles.sectionHeader}>Environments</Text>
            <DefaultButton
              text="Manage"
              iconProps={{ iconName: 'Settings' }}
              onClick={() => setEnvPanelOpen(true)}
              styles={{ root: { minWidth: 0 } }}
            />
          </div>
          <EnvironmentPicker
            workspaceCode={undefined}
            value={environmentConfigs}
            onChange={setEnvironmentConfigs}
            readOnly
            userEmail={userEmail}
          />
        </div>

        <Panel
          isOpen={envPanelOpen}
          type={PanelType.medium}
          onDismiss={() => setEnvPanelOpen(false)}
          headerText="Manage Environments"
          onRenderFooterContent={() => (
            <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { padding: '12px 0' } }}>
              <PrimaryButton text="Save" onClick={() => setEnvPanelOpen(false)} />
              <DefaultButton text="Cancel" onClick={() => setEnvPanelOpen(false)} />
            </Stack>
          )}
          isFooterAtBottom
        >
          <div style={{ paddingTop: 16 }}>
            <EnvironmentPicker
              workspaceCode={undefined}
              value={environmentConfigs}
              onChange={setEnvironmentConfigs}
              userEmail={userEmail}
              showLifecycle
            />
          </div>
        </Panel>

        {/* Sessions section */}
        <div className={styles.section}>
          <Text variant="mediumPlus" className={styles.sectionHeader}>Sessions</Text>
          <PrimaryButton text={creatingSess ? 'Creating…' : '+ New Session'} onClick={handleCreateSession} disabled={creatingSess} styles={{ root: { alignSelf: 'flex-start' } }} />
          {sessions.length === 0 ? (
            <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>No sessions yet. Create one to get started.</Text>
          ) : (
            <div className={styles.sessionList}>
              {sessions.map((s) => (
                <div key={s.name} className={styles.sessionRow}>
                  <div className={styles.sessionInfo}>
                    <Text styles={{ root: { fontWeight: 600 } }}>{s.name}</Text>
                    <Text variant="tiny" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
                      {s.windows} {s.windows === 1 ? 'window' : 'windows'}
                      {s.copilotRunning && ' · 🤖 Copilot'}
                      {s.createdAt > 0 && ` · ${formatAge(s.createdAt)}`}
                    </Text>
                  </div>
                  <Stack horizontal tokens={{ childrenGap: 4 }}>
                    <Link to={`/instances/${id}/terminal/sessions/${s.name}`} style={{ textDecoration: 'none' }}>
                      <PrimaryButton text="Open" styles={{ root: { minWidth: 0, padding: '4px 12px' } }} />
                    </Link>
                    <IconButton iconProps={{ iconName: 'Delete' }} title="Delete session" onClick={() => handleDeleteSession(s.name)} styles={{ root: { color: 'var(--ab-error)' } }} />
                  </Stack>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SSH section */}
        <div className={styles.section}>
          <Text variant="mediumPlus" className={styles.sectionHeader}>SSH</Text>
          <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
            Generate a signed certificate keypair to connect from any SSH client.
          </Text>
          <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { flexWrap: 'wrap' } }}>
            <PrimaryButton
              text={sshConnecting ? 'Generating…' : 'Connect via SSH'}
              iconProps={sshConnecting ? undefined : { iconName: 'Lock' }}
              onClick={handleSshConnect}
              disabled={sshConnecting}
            />
            <DefaultButton text={copied ? '✓ Copied' : 'Copy SSH Config'} onClick={copySshConfig} iconProps={{ iconName: 'Copy' }} />
          </Stack>

          {sshInfo && (
            <div className={styles.sshConnectBox}>
              <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Your credentials are ready</Text>
              <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
                Download both files, then connect with: <code>ssh -i agentbox.pem -o CertificateFile=agentbox-cert.pub -p 2222 agentbox@{box.fqdn}</code>
              </Text>
              <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { flexWrap: 'wrap' } }}>
                <PrimaryButton
                  text="⬇ Download agentbox.pem"
                  onClick={() => downloadText('agentbox.pem', sshInfo.privateKeyPem)}
                  iconProps={{ iconName: 'Download' }}
                />
                <PrimaryButton
                  text="⬇ Download agentbox-cert.pub"
                  onClick={() => downloadText('agentbox-cert.pub', sshInfo.certificate + '\n')}
                  iconProps={{ iconName: 'Download' }}
                />
              </Stack>
              <Text variant="tiny" styles={{ root: { color: 'var(--ab-text-subtle)' } }}>
                Certificate is valid for 24 hours. Generate a new one when it expires.
              </Text>
            </div>
          )}
        </div>

        {destroying && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} className={styles.destroyingCard}>
            <Spinner size={SpinnerSize.small} />
            <Text>Destroying instance…</Text>
          </Stack>
        )}
      </div>
    </div>
  );
}
